import { css } from 'aphrodite';
import * as React from 'react';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { ThreadType } from '../../../models/Support/SupportEnums';
import { useSupportTeamPresentation } from '../../../queries/Support/useSupportTeam';
import { useSupportThreadsQuery } from '../../../queries/Support/useSupportThreadsQuery';
import { useMergeTicketMutation } from '../../../queries/Support/useSupportTicketMutations';
import { useSupportTicketQuery } from '../../../queries/Support/useSupportTicketQuery';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { ticketPreviewModalStyles as modalStyles } from './styles/ticketPreviewModalStyles';
import { formatDate, getTicketAssigneeName } from './supportTicketPresentation';
import { ThreadItem } from './ThreadItem';
import './styles/threadBodyStyles.css';

const MAX_PREVIEW_THREADS = 5;

interface TicketPreviewModalProps {
	/** The related ticket being previewed. */
	sourceTicketId: string;
	/** The currently selected ticket; merge moves source threads into this ticket. */
	targetTicketId: string;
	onClose: () => void;
	onMergeSuccess: () => void;
	onOpenTicket: (ticketId: string) => void;
}

/** Preview a previous conversation from the ticket details drawer without leaving the current ticket. */
export const TicketPreviewModal: React.FC<TicketPreviewModalProps> = ({
	sourceTicketId,
	targetTicketId,
	onClose,
	onMergeSuccess,
	onOpenTicket,
}) => {
	const userSession = useAdminUserSession();
	const currentUser = userSession.user;
	const currentUserName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ');
	const { data: ticket, isLoading: ticketLoading } = useSupportTicketQuery(sourceTicketId, { refetchInterval: false });
	const { data: threads, isLoading: threadsLoading } = useSupportThreadsQuery(sourceTicketId, {
		refetchInterval: false,
	});
	const mergeMutation = useMergeTicketMutation(targetTicketId);
	const { agentNameMap, agentProfilePicMap, agentProfilePicEmailMap } = useSupportTeamPresentation(
		ticket?.mailboxId || ''
	);
	const [showMergeConfirm, setShowMergeConfirm] = React.useState(false);
	const [mergeError, setMergeError] = React.useState('');

	const visibleThreads = (threads?.values ?? [])
		.filter(thread => thread.type !== ThreadType.AiDraft)
		.slice(0, MAX_PREVIEW_THREADS);
	const assigneeName = ticket ? getTicketAssigneeName(ticket, agentNameMap) : undefined;

	const confirmMerge = () => {
		setShowMergeConfirm(false);
		setMergeError('');
		mergeMutation.mutate(sourceTicketId, {
			onSuccess: onMergeSuccess,
			onError: () => setMergeError('Failed to merge ticket. Please try again.'),
		});
	};

	return (
		<>
			<Dialog
				open
				onOpenChange={open => {
					if (!open) onClose();
				}}
			>
				<DialogPopup styles={[modalStyles.panel]}>
					<DialogHeader styles={[modalStyles.header]}>
						{ticketLoading ? (
							<DialogTitle styles={[modalStyles.headerSubject]}>Loading...</DialogTitle>
						) : (
							<div className={css(modalStyles.headerRow)}>
								<DialogTitle styles={[modalStyles.headerSubject]}>{ticket?.subject || 'Untitled'}</DialogTitle>
								{ticket && <span className={css(modalStyles.statusBadge)}>{ticket.status}</span>}
							</div>
						)}
					</DialogHeader>

					<DialogBody styles={[modalStyles.body]}>
						{ticketLoading ? (
							<div className={css(modalStyles.noThreads)}>
								<LoadingSpinner type='small' />
							</div>
						) : ticket ? (
							<>
								<div className={css(modalStyles.metaRow)}>
									<span>
										<span className={css(modalStyles.metaLabel)}>Ticket: </span>#{ticket.number}
									</span>
									{assigneeName && (
										<span>
											<span className={css(modalStyles.metaLabel)}>Assignee: </span>
											{assigneeName}
										</span>
									)}
									<span>
										<span className={css(modalStyles.metaLabel)}>Created: </span>
										{formatDate(ticket.creationDate)}
									</span>
								</div>

								{ticket.previewText && <div className={css(modalStyles.previewText)}>{ticket.previewText}</div>}

								{threadsLoading ? (
									<div className={css(modalStyles.noThreads)}>
										<LoadingSpinner type='small' />
									</div>
								) : visibleThreads.length > 0 ? (
									<div className={css(modalStyles.threadList)}>
										{visibleThreads.map(thread => (
											<ThreadItem
												key={thread.id}
												thread={thread}
												ticket={ticket}
												agentNameMap={agentNameMap}
												agentProfilePicMap={agentProfilePicMap}
												agentProfilePicEmailMap={agentProfilePicEmailMap}
												currentUserId={currentUser.id}
												currentUserName={currentUserName}
												currentUserEmail={currentUser.email}
												currentUserProfilePic={currentUser.profilePic}
												currentAccountId={userSession.account?.id}
											/>
										))}
									</div>
								) : (
									<div className={css(modalStyles.noThreads)}>No threads found</div>
								)}
							</>
						) : (
							<div className={css(modalStyles.noThreads)}>Ticket not found</div>
						)}
					</DialogBody>

					<DialogFooter styles={[modalStyles.footer]}>
						{mergeError && <div className={css(modalStyles.mergeError)}>{mergeError}</div>}
						<Button
							variant='outline'
							size='sm'
							onClick={() => setShowMergeConfirm(true)}
							disabled={!ticket || mergeMutation.isPending || sourceTicketId === targetTicketId}
						>
							{mergeMutation.isPending ? 'Merging...' : 'Merge into Current'}
						</Button>
						<Button
							variant='primary'
							size='sm'
							onClick={() => {
								onOpenTicket(sourceTicketId);
								onClose();
							}}
							disabled={!ticket}
						>
							Open
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
			{showMergeConfirm && ticket && (
				<ConfirmModal
					title='Merge Ticket'
					message={
						`Merge ticket #${ticket.number} into this ticket? All threads from #${ticket.number} will be moved ` +
						`here and #${ticket.number} will be closed.`
					}
					confirmLabel='Merge'
					variant='danger'
					onConfirm={confirmMerge}
					onCancel={() => setShowMergeConfirm(false)}
				/>
			)}
		</>
	);
};
